import React, { useEffect, useMemo, useState } from 'react';
import { useStateContext } from 'shared/contexts/ContextProvider';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  Wallet,
  TrendingUp,
  CalendarDays,
  Loader2,
  Inbox,
} from 'lucide-react';
import { BASE_URL } from 'shared/config';
// BASE_URL is assumed to already be available in your project (same constant
// used elsewhere for API calls). Import it from wherever it lives, e.g.:
// import { BASE_URL } from 'shared/config';

// ---------------------------------------------------------------------------
// Palette — flat, restrained, standard ERP register
// ---------------------------------------------------------------------------
const C = {
  bg: '#F3F5F8',
  card: '#FFFFFF',
  border: '#E4E8EF',
  text: '#1B2430',
  textSoft: '#6B7686',
  textFaint: '#9AA4B2',
  primary: '#3457D5',
  primarySoft: '#EAEFFC',
  green: '#1AA179',
  greenSoft: '#E6F5F0',
  amber: '#C98A1F',
  amberSoft: '#FBF1DF',
  red: '#D2483A',
  redSoft: '#FBEAE8',
};

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_RU_FULL = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const WEEKDAYS_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MONTHS_RU_NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const num = (n, decimals = 0) =>
  Number(n).toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const money = (n) => `${num(Math.round(Number(n) / 10) * 10, 0)} сом`;

const pct = (n, decimals = 1) => `${Number(n).toFixed(decimals)}%`;

const parseDate = (iso) => new Date(`${iso}T00:00:00`);
const dayLabel = (iso) => {
  const d = parseDate(iso);
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
};
const weekdayLabel = (iso) => WEEKDAYS_RU[parseDate(iso).getDay()];

const initials = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

// ---------------------------------------------------------------------------
// UI pieces
// ---------------------------------------------------------------------------
function KpiCard({ icon: Icon, label, value, sub, tint = 'primary' }) {
  const tints = {
    primary: { bg: C.primarySoft, fg: C.primary },
    green: { bg: C.greenSoft, fg: C.green },
    amber: { bg: C.amberSoft, fg: C.amber },
    neutral: { bg: '#EEF0F4', fg: C.textSoft },
  };
  const t = tints[tint];
  return (
    <div className="rounded-xl p-4 sm:p-5 flex flex-col gap-3" style={{ background: C.card, border: `1px solid ${C.border}` }}>
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-medium" style={{ color: C.textSoft }}>
          {label}
        </span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.bg, color: t.fg }}>
          <Icon size={16} strokeWidth={2} />
        </div>
      </div>
      <div className="text-[22px] sm:text-[24px] leading-none font-semibold" style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && (
        <div className="text-[12.5px]" style={{ color: C.textFaint }}>
          {sub}
        </div>
      )}
    </div>
  );
}

const ChartTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-[12.5px] shadow-sm" style={{ background: C.card, border: `1px solid ${C.border}` }}>
      <div className="mb-1 font-semibold" style={{ color: C.text }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 justify-between">
          <span className="flex items-center gap-1.5" style={{ color: C.textSoft }}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
            {p.name}
          </span>
          <span style={{ color: C.text, fontWeight: 600 }}>
            {mode === 'revenue' || mode === 'avgCheck' ? money(p.value, 0) : num(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

function StateScreen({ icon: Icon, spin, title, subtitle }) {
  return (
    <div className="min-h-full w-full flex items-center justify-center" style={{ background: C.bg, fontFamily: "'Inter', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>
      <div className="flex flex-col items-center gap-3 py-24">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: C.primarySoft, color: C.primary }}>
          <Icon size={18} className={spin ? 'animate-spin' : ''} />
        </div>
        <div className="text-[14px] font-medium" style={{ color: C.text }}>
          {title}
        </div>
        {subtitle && (
          <div className="text-[13px]" style={{ color: C.textFaint }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
const pad = (n) => String(n).padStart(2, '0');
const monthStart = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
const monthEnd = (d = new Date()) => {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); // day 0 = last day of this month
  return `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
};


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ConsultantDashboard({
  fromDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('en-CA'),
  toDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString('en-CA'),
} = {}) {
  const { showToast, token } = useStateContext();
  const [stats, setStats] = useState();
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState('revenue');

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(BASE_URL + `/admin-api/consultants/stats/?from_date=${fromDate}&to_date=${toDate}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        showToast({
          title: 'Error!',
          content: data.message,
          cssClass: 'e-toast-danger',
          icon: 'e-error toast-icons',
        });
        return;
      }
      const data = await response.json();
      setStats(data.response);
    } catch (error) {
      showToast({
        title: 'Error!',
        content: 'Failed to load order stats.',
        cssClass: 'e-toast-danger',
        icon: 'e-error toast-icons',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadStats();
    }
  }, [token, fromDate, toDate]);

  const days = useMemo(() => {
    if (!stats?.stats) return [];
    return [...stats.stats].sort((a, b) => (a.day_date > b.day_date ? 1 : -1));
  }, [stats]);

  const totals = stats?.totals;
  const consultant = stats?.consultant?.meta;

  const derived = useMemo(() => {
    if (!totals || days.length === 0) return null;
    const n = days.length;
    const avgCheck = totals.total_sum / totals.total_count;
    const avgDailyRevenue = totals.total_sum / n;
    const avgDailyCount = totals.total_count / n;

    const rows = days.map((d) => ({
      ...d,
      avgCheck: d.day_sum / d.day_count,
      label: dayLabel(d.day_date),
      wk: weekdayLabel(d.day_date),
    }));

    const bestDay = rows.reduce((a, b) => (b.day_sum > a.day_sum ? b : a), rows[0]);

    const chartData = rows.map((r) => ({
      label: r.label,
      sum: Math.round(r.day_sum / 10) * 10,
      count: r.day_count,
      avgCheck: Math.round(r.avgCheck / 10) * 10,
    }));

    return { avgCheck, avgDailyRevenue, avgDailyCount, rows, bestDay, chartData };
  }, [days, totals]);

  const currentMonthLabel = useMemo(() => {
    if (!fromDate) return '';
    const parts = fromDate.split('-');
    if (parts.length < 2) return '';
    const monthIdx = parseInt(parts[1], 10) - 1;
    const year = parts[0];
    const monthName = MONTHS_RU_NOM[monthIdx] || '';
    return `показатели за ${monthName} ${year}`;
  }, [fromDate]);

  const periodLabel = useMemo(() => {
    if (!days.length) return '—';
    const first = parseDate(days[0].day_date);
    const last = parseDate(days[days.length - 1].day_date);
    return `${first.getDate()}–${last.getDate()} ${MONTHS_RU_FULL[last.getMonth()]} ${last.getFullYear()}`;
  }, [days]);

  // ---- Loading / empty states ----
  if (loading) {
    return <StateScreen icon={Loader2} spin title="Загрузка данных…" subtitle="Получаем показатели консультанта" />;
  }
  if (!stats || !totals || days.length === 0) {
    return <StateScreen icon={Inbox} title="Нет данных за выбранный период" subtitle="Попробуйте выбрать другой диапазон дат" />;
  }

  return (
    <div className="min-h-full w-full" style={{ background: C.bg, fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .cd-seg-btn:focus-visible { outline: 2px solid ${C.primary}; outline-offset: 1px; }
        .cd-row:hover { background: #FAFBFC; }
        @media (prefers-reduced-motion: no-preference) {
          .cd-fade { animation: cdFade .35s ease both; }
        }
        @keyframes cdFade { from { opacity: 0; transform: translateY(6px);} to { opacity: 1; transform: translateY(0);} }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-9">
        {/* ---------------- Header ---------------- */}
        <div className="cd-fade flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-[14px] font-semibold"
              style={{ background: C.primary, color: '#fff' }}
            >
              {initials(consultant?.name)}
            </div>
            <div>
              <h1 className="text-[17px] sm:text-[19px] font-semibold leading-tight" style={{ color: C.text }}>
                {consultant?.name || 'Консультант'}
              </h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[12.5px]" style={{ color: C.textSoft }}>
                  Консультант по продажам
                </span>
                <span className="text-[12.5px]" style={{ color: C.textFaint }}>
                  · ID {consultant?.id ?? '—'}
                </span>
              </div>
            </div>
          </div>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg self-start sm:self-auto text-[13px]"
            style={{ background: C.card, border: `1px solid ${C.border}`, color: C.textSoft }}
          >
            <CalendarDays size={15} />
            {periodLabel} · {days.length} {days.length === 1 ? 'день' : 'дней'}
          </div>
        </div>

        {/* ---------------- Summary bar ---------------- */}
        <div className="cd-fade rounded-xl p-5 sm:p-6 mb-6" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <div>
            <div className="text-[12.5px] font-medium mb-1.5" style={{ color: C.textSoft }}>
              Выручка за период
            </div>
            <div className="text-[32px] sm:text-[36px] font-semibold leading-none" style={{ color: C.text }}>
              {money(totals.total_sum)}
            </div>
            <div className="text-[13px] mt-2" style={{ color: C.textFaint }}>
              {totals.total_count} продаж
            </div>
          </div>
        </div>

        {/* ---------------- KPI grid ---------------- */}
        <div className="cd-fade grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
          <KpiCard icon={TrendingUp} label="Выручка" value={money(totals.total_sum)} sub="всего за период" tint="primary" />
          <KpiCard icon={Users} label="Транзакции" value={num(totals.total_count)} sub={`~${derived.avgDailyCount.toFixed(1)} в день`} tint="primary" />
          <KpiCard icon={Wallet} label="Средний чек" value={money(derived.avgCheck)} sub="на одну сделку" tint="primary" />
        </div>

        {/* ---------------- Trend chart ---------------- */}
        <div className="cd-fade rounded-xl p-5 sm:p-6 mb-6" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h2 className="text-[14.5px] font-semibold" style={{ color: C.text }}>
              Динамика по дням
            </h2>
            <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
              {[
                { key: 'revenue', label: 'Выручка' },
                { key: 'volume', label: 'Продажи' },
                { key: 'avgCheck', label: 'Средний чек' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setChartMode(opt.key)}
                  className="cd-seg-btn px-3 py-1.5 text-[12.5px] font-medium transition-colors"
                  style={{
                    color: chartMode === opt.key ? '#fff' : C.textSoft,
                    background: chartMode === opt.key ? C.primary : 'transparent',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <ComposedChart data={derived.chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: C.textFaint, fontSize: 12 }} axisLine={{ stroke: C.border }} tickLine={false} />
                <YAxis tick={{ fill: C.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip mode={chartMode} />} cursor={{ fill: '#F3F5F8' }} />
                {chartMode === 'revenue' && (
                  <Bar dataKey="sum" name="Выручка" fill={C.primary} radius={[5, 5, 0, 0]} maxBarSize={44} />
                )}
                {chartMode === 'volume' && (
                  <Bar dataKey="count" name="Продажи" fill={C.textFaint} radius={[5, 5, 0, 0]} maxBarSize={44} />
                )}
                {chartMode === 'avgCheck' && (
                  <Bar dataKey="avgCheck" name="Средний чек" fill={C.amber} radius={[5, 5, 0, 0]} maxBarSize={44} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ---------------- Daily table ---------------- */}
        <div className="cd-fade rounded-xl p-4 sm:p-6 mb-6" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <h2 className="text-[14.5px] font-semibold mb-4" style={{ color: C.text }}>
            Таблица по дням
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {[
                    { label: 'Дата', className: 'text-left pr-2 sm:pr-4' },
                    { label: 'Продажи', className: 'text-right px-1 sm:px-3' },
                    { label: 'Выручка', className: 'text-right px-1 sm:px-3' },
                    { label: 'Ср. чек', labelFull: 'Средний чек', className: 'text-right pl-1 sm:pl-3' },
                  ].map((col) => (
                    <th
                      key={col.label}
                      className={`py-2 text-[11.5px] sm:text-[12px] font-medium whitespace-nowrap ${col.className}`}
                      style={{ color: C.textFaint }}
                    >
                      {col.labelFull ? (
                        <>
                          <span className="hidden sm:inline">{col.labelFull}</span>
                          <span className="inline sm:hidden">{col.label}</span>
                        </>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {derived.rows.map((r) => {
                  const isBest = r.day_date === derived.bestDay.day_date;
                  return (
                    <tr key={r.day_date} className="cd-row" style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td className="py-2.5 pr-2 sm:pr-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="font-medium text-[12.5px] sm:text-[13.5px]" style={{ color: C.text }}>
                            {r.label}
                          </span>
                          <span className="text-[11px] sm:text-[12px]" style={{ color: C.textFaint }}>{r.wk}</span>
                          {isBest && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: C.green }} title="Лучший день" />}
                        </div>
                      </td>
                      <td className="py-2.5 px-1 sm:px-3 text-right text-[12.5px] sm:text-[13.5px]" style={{ color: C.textSoft }}>
                        {r.day_count}
                      </td>
                      <td className="py-2.5 px-1 sm:px-3 text-right text-[12.5px] sm:text-[13.5px] font-medium" style={{ color: C.text }}>
                        {num(Math.round(r.day_sum / 10) * 10, 0)}
                      </td>
                      <td className="py-2.5 pl-1 sm:pl-3 text-right text-[12.5px] sm:text-[13.5px] font-medium" style={{ color: C.textSoft }}>
                        {num(Math.round(r.avgCheck / 10) * 10, 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="py-3 pr-2 sm:pr-4 font-semibold text-[13px] sm:text-[13.5px]" style={{ color: C.text }}>
                    Итого
                  </td>
                  <td className="py-3 px-1 sm:px-3 text-right font-semibold text-[13px] sm:text-[13.5px]" style={{ color: C.text }}>
                    {num(totals.total_count)}
                  </td>
                  <td className="py-3 px-1 sm:px-3 text-right font-semibold text-[13px] sm:text-[13.5px]" style={{ color: C.text }}>
                    {num(Math.round(totals.total_sum / 10) * 10, 0)}
                  </td>
                  <td className="py-3 pl-1 sm:pl-3 text-right font-semibold text-[13px] sm:text-[13.5px]" style={{ color: C.text }}>
                    {num(Math.round(derived.avgCheck / 10) * 10, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <span className="flex items-center gap-1.5 text-[12px]" style={{ color: C.textFaint }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: C.green }} /> лучший день
            </span>
          </div>
        </div>

        <div className="cd-fade text-center text-[12px] pt-5" style={{ color: C.textFaint }}>
          Сотрудник №{consultant?.id ?? '—'} · {currentMonthLabel}
        </div>
      </div>
    </div>
  );
}